/**
 * Reading a drag or a paste, with no DOM and no React — so the panel's handlers
 * stay short enough to see the event plumbing that actually matters.
 *
 * These functions deliberately do NOT decide which files are acceptable. The
 * picker, the drop target and the clipboard must answer that question
 * identically, so it is answered exactly once, in the panel's `acceptFiles`.
 * Here we only say what the gesture CARRIED.
 *
 * Two browser facts shape everything else:
 *
 * 1. During `dragover` the payload is NOT readable. `DataTransfer.files` is
 *    empty until `drop`; only `types` is populated. So "should I light up the
 *    drop target?" and "what did I just receive?" are genuinely different
 *    questions and get different functions.
 * 2. A dragged image FROM A WEB PAGE is a URL, not a File. Fetching it in the
 *    browser is not an option we declined for tidiness — `connect-src` forbids
 *    it, and pointing a child's browser at an arbitrary host on their own home
 *    network is the exact attack `isSafePreviewImage` exists to refuse. A URL
 *    drop becomes text in the composer, which is the honest thing it is.
 */

export type DropReading =
  | { kind: 'files'; files: File[] }
  | { kind: 'url'; url: string }
  | { kind: 'empty' }

/**
 * Does this drag carry files? Answered from `types` alone, because that is all
 * a `dragover` gives us. Dragging selected TEXT across the panel must not flash
 * the drop target, so this is deliberately narrow.
 */
export function dragHasFiles(transfer: DataTransfer | null): boolean {
  if (!transfer) return false
  // `types` is a DOMStringList in older engines and an array in current ones;
  // `includes` is not available on the former.
  for (let index = 0; index < transfer.types.length; index += 1) {
    if (transfer.types[index] === 'Files') return true
  }
  return false
}

/**
 * A dropped folder arrives as an entry with an empty `type`, which the panel's
 * MIME filter discards the same way it discards a PDF — which is why there is
 * no explicit directory branch here.
 */
export function readDrop(transfer: DataTransfer | null): DropReading {
  if (!transfer) return { kind: 'empty' }

  const files = Array.from(transfer.files ?? [])
  if (files.length > 0) return { kind: 'files', files }

  const uri = firstUri(transfer.getData('text/uri-list') || transfer.getData('text/plain'))
  if (uri) return { kind: 'url', url: uri }
  return { kind: 'empty' }
}

/**
 * The clipboard. A screenshot pasted into the composer is the gesture a tutor
 * reaches for most, and it is also the ONLY one of these gestures that works on
 * Android — HTML5 drag and drop does not exist there.
 *
 * Text pastes are left entirely alone: hijacking a paste of ordinary text would
 * break typing, which is the composer's actual job.
 */
export function readPaste(data: DataTransfer | null): DropReading {
  if (!data) return { kind: 'empty' }
  const files = Array.from(data.files ?? [])
  if (files.length === 0) return { kind: 'empty' }
  return { kind: 'files', files }
}

/** `text/uri-list` is newline-separated and may carry `#` comment lines. */
function firstUri(raw: string): string | null {
  if (!raw) return null
  for (const line of raw.split(/\r?\n/)) {
    const candidate = line.trim()
    if (!candidate || candidate.startsWith('#')) continue
    if (/^https?:\/\//i.test(candidate)) return candidate.slice(0, 2048)
    return null
  }
  return null
}
